import { useRef, useState } from "react";
import { UploadCloud, X, Star, Loader2 } from "lucide-react";
import { imageUrl } from "../../data/constants";
import { toast } from "sonner";
import api from "../../lib/api";

export function ImageUploader({ images, primary, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setUploading(true);
    const added = [];
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.path);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    const next = [...images, ...added];
    onChange(next, primary || next[0]);
    setUploading(false);
    if (added.length) toast.success(`${added.length} image(s) uploaded`);
  };

  const removeImg = (path) => {
    const next = images.filter((p) => p !== path);
    onChange(next, primary === path ? next[0] || null : primary);
  };

  return (
    <div>
      <div
        data-testid="admin-image-drag-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-[#10B981] bg-[#ECFDF5]" : "border-slate-300 bg-slate-50 hover:border-[#10B981]"}`}
      >
        {uploading ? <Loader2 className="h-7 w-7 animate-spin text-[#10B981]" /> : <UploadCloud className="h-7 w-7 text-slate-400" />}
        <p className="text-sm font-semibold text-slate-700">Drag & drop images or click to upload</p>
        <p className="text-xs text-slate-400">JPG, PNG, WEBP — multiple allowed</p>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
          onChange={(e) => uploadFiles(e.target.files)} data-testid="admin-image-file-input" />
      </div>

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((path) => (
            <div key={path} className={`group relative aspect-square overflow-hidden rounded-xl border-2 ${primary === path ? "border-[#10B981]" : "border-slate-200"}`}>
              <img src={imageUrl(path)} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => onChange(images, path)} title="Set as cover"
                  data-testid="admin-image-set-cover-btn"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-amber-500 hover:scale-110">
                  <Star className={`h-4 w-4 ${primary === path ? "fill-amber-500" : ""}`} />
                </button>
                <button type="button" onClick={() => removeImg(path)} title="Remove"
                  data-testid="admin-image-remove-btn"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-red-500 hover:scale-110">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {primary === path && <span className="absolute left-1 top-1 rounded-full bg-[#10B981] px-2 py-0.5 text-[9px] font-bold uppercase text-white">Cover</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
